const nlhReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const nlhRevealSelector = [
  '.nlh-hero__content',
  '.nlh-advantage',
  '.nlh-section-heading',
  '.nlh-category-card',
  '.nlh-program__media',
  '.nlh-program__content',
  '.nlh-factory__heading',
  '.nlh-factory__media',
  '.nlh-factory__proof',
  '.nlh-faq__heading',
  '.nlh-faq__item',
  '.nlh-footer__main > *',
].join(',');

let nlhRevealObserver;
const nlhHeroRotators = new WeakMap();
let nlhQuoteCenterObserver;
let nlhQuoteCenterFrame;

function nlhInitializeHeroRotator(rotator) {
  nlhHeroRotators.get(rotator)?.destroy?.();

  const slides = Array.from(rotator.querySelectorAll('[data-nlh-hero-slide]'));
  const dots = Array.from(rotator.querySelectorAll('[data-nlh-hero-dot]'));
  const previous = rotator.querySelector('[data-nlh-hero-prev]');
  const next = rotator.querySelector('[data-nlh-hero-next]');
  const pause = rotator.querySelector('[data-nlh-hero-pause]');
  const autoplay = rotator.dataset.autoplay === 'true' && !nlhReducedMotion.matches;
  const interval = Math.max(Number(rotator.dataset.interval) || 6000, 4000);

  if (slides.length < 2) return;

  let activeIndex = Math.max(slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
  let pausedByUser = false;
  let timer;
  const cleanups = [];

  const show = (index) => {
    activeIndex = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });
    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', String(isActive));
    });
  };

  const stop = () => {
    window.clearInterval(timer);
    timer = undefined;
  };

  const start = () => {
    stop();
    if (!autoplay || pausedByUser || document.hidden) return;
    timer = window.setInterval(() => show(activeIndex + 1), interval);
  };

  const listen = (target, event, handler) => {
    if (!target) return;
    target.addEventListener(event, handler);
    cleanups.push(() => target.removeEventListener(event, handler));
  };

  listen(previous, 'click', () => {
    show(activeIndex - 1);
    start();
  });
  listen(next, 'click', () => {
    show(activeIndex + 1);
    start();
  });
  dots.forEach((dot, index) => listen(dot, 'click', () => {
    show(index);
    start();
  }));
  listen(pause, 'click', () => {
    pausedByUser = !pausedByUser;
    pause.classList.toggle('is-paused', pausedByUser);
    pause.setAttribute('aria-pressed', String(pausedByUser));
    pause.setAttribute('aria-label', pausedByUser ? 'Resume product rotation' : 'Pause product rotation');
    start();
  });
  listen(rotator, 'pointerenter', stop);
  listen(rotator, 'pointerleave', start);
  listen(rotator, 'focusin', stop);
  listen(rotator, 'focusout', (event) => {
    if (!rotator.contains(event.relatedTarget)) start();
  });
  listen(document, 'visibilitychange', start);

  const editorSelect = (event) => {
    const selectedSlide = rotator.querySelector(`[data-nlh-slide-id="${event.detail.blockId}"]`);
    if (!selectedSlide) return;
    show(Number(selectedSlide.dataset.slideIndex));
    stop();
  };
  listen(document, 'shopify:block:select', editorSelect);
  listen(document, 'shopify:block:deselect', start);

  show(activeIndex);
  start();

  nlhHeroRotators.set(rotator, {
    destroy() {
      stop();
      cleanups.forEach((cleanup) => cleanup());
    },
  });
}

function nlhPrepareReveal(root = document) {
  const elements = root.querySelectorAll(nlhRevealSelector);
  const designMode = document.documentElement.classList.contains('shopify-design-mode');

  elements.forEach((element, index) => {
    element.dataset.nlhReveal = '';
    element.style.setProperty('--nlh-reveal-delay', `${Math.min(index % 6, 5) * 55}ms`);

    if (designMode || nlhReducedMotion.matches || !('IntersectionObserver' in window)) {
      element.classList.add('is-nlh-visible');
      return;
    }

    nlhRevealObserver.observe(element);
  });
}

function nlhSetHeaderState() {
  const header = document.querySelector('#header-component');
  if (!header) return;
  header.classList.toggle('nlh-header-scrolled', window.scrollY > 18);
}

function nlhLocalizeHeaderLabels() {
  const header = document.querySelector('#header-component');
  if (!header) return;

  const labels = new Map([
    ['/', 'Home'],
    ['/collections/all', 'Catalog'],
    ['/pages/product-quote-center', 'Product Quote Center'],
    ['/pages/contact', 'Contact'],
  ]);

  header.querySelectorAll('a[href]').forEach((link) => {
    const label = labels.get(new URL(link.href, window.location.origin).pathname);
    const labelNode = link.querySelector('.menu-list__link-title, .menu-drawer__menu-item-text');
    if (label && labelNode && labelNode.textContent.trim() !== label) {
      labelNode.textContent = label;
    }
  });
}

function nlhEnsureQuoteCenterHeaderLink() {
  const menu = document.querySelector('#header-component header-menu overflow-list');
  if (!menu) return;

  const quoteLinks = Array.from(menu.querySelectorAll('a[href*="/pages/product-quote-center"]'));
  if (quoteLinks.length) {
    quoteLinks.slice(1).forEach((link) => link.closest('li')?.remove());
    nlhLocalizeHeaderLabels();
    return;
  }

  const listItem = document.createElement('li');
  listItem.className = 'menu-list__list-item';
  listItem.dataset.nlhQuoteCenterEntry = 'true';
  listItem.setAttribute('role', 'presentation');

  const link = document.createElement('a');
  link.href = '/pages/product-quote-center';
  link.className = 'menu-list__link';
  link.dataset.skipNodeUpdate = 'true';
  link.setAttribute('ref', 'menuitem');
  if (window.location.pathname === '/pages/product-quote-center') {
    link.classList.add('menu-list__link--active');
    link.setAttribute('aria-current', 'page');
  }

  const title = document.createElement('span');
  title.className = 'menu-list__link-title';
  title.textContent = 'Product Quote Center';
  link.append(title);
  listItem.append(link);

  const contactItem = menu.querySelector('a[href="/pages/contact"]')?.closest('li');
  const moreItem = menu.querySelector('li[slot="more"]');
  menu.insertBefore(listItem, contactItem || moreItem || null);
}

function nlhScheduleQuoteCenterHeaderLink() {
  if (nlhQuoteCenterFrame) return;
  nlhQuoteCenterFrame = window.requestAnimationFrame(() => {
    nlhQuoteCenterFrame = undefined;
    nlhLocalizeHeaderLabels();
    nlhEnsureQuoteCenterHeaderLink();
  });
}

function nlhObserveQuoteCenterHeaderLink() {
  nlhEnsureQuoteCenterHeaderLink();
  if (nlhQuoteCenterObserver || !document.body) return;

  nlhQuoteCenterObserver = new MutationObserver(nlhScheduleQuoteCenterHeaderLink);
  nlhQuoteCenterObserver.observe(document.body, { childList: true, subtree: true });
}

function nlhInitialize(root = document) {
  if (!nlhRevealObserver) {
    nlhRevealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-nlh-visible');
        nlhRevealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  }

  nlhPrepareReveal(root);
  root.querySelectorAll('[data-nlh-hero-rotator]').forEach(nlhInitializeHeroRotator);
  nlhSetHeaderState();
  nlhLocalizeHeaderLabels();
  nlhObserveQuoteCenterHeaderLink();
}

document.documentElement.classList.add('nlh-motion-ready');

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => nlhInitialize());
} else {
  nlhInitialize();
}

let nlhScrollFrame;
window.addEventListener('scroll', () => {
  if (nlhScrollFrame) return;
  nlhScrollFrame = window.requestAnimationFrame(() => {
    nlhSetHeaderState();
    nlhScrollFrame = undefined;
  });
}, { passive: true });

document.addEventListener('shopify:section:load', (event) => nlhInitialize(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-nlh-hero-rotator]').forEach((rotator) => {
    nlhHeroRotators.get(rotator)?.destroy?.();
    nlhHeroRotators.delete(rotator);
  });
});
